/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import character from '../../assets/images/intake/step2/char.svg';
import { useUserAsaTaskHistoryContext } from '../../hooks/use-user-asa-task-history-context.hooks';
import { useAuthContext } from '../../hooks/use-user-auth-context.hooks';
import {
  AsaTaskName,
  AsaUserTasks,
  completeASATask,
  dispatchUpdateAsaTaskHistoryInfo,
} from '../../redux/slice/asa-task-history';
import { CustomUser } from '../../redux/slice/auth/CustomUser';
import {
  CareerRoadMapDto,
  proficiency_options,
  ScorePhyEnvDto,
} from '../../redux/slice/career-roadmap/career-roadmap-type';
import { dispatchCareerRoadMapAssessment } from '../../redux/slice/career-roadmap/career-roadmap.operations';
import { roadMapQuestions } from '../../redux/slice/career-roadmap/careerRoadMapQuestions';
import { RootState, useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';
import { EnvironmentVariable } from '../../utils/constants';
import { buildASANextTaskUrl } from '../../utils/helper';
import { CircularLoader } from '../loader/CircularLoader';

interface ComponentProps {
  step: number;
  onBack: () => void;
}

interface FormData extends ScorePhyEnvDto {}

export const PhysicalityScore: React.FC<ComponentProps> = ({ step, onBack }) => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const dispatch = useAppDispatch();
  const physicalityState = useSelector((state: RootState) => state.careerRoadmap.physicality_score);
  const facilityState = useSelector((state: RootState) => state.careerRoadmap.facility_environment);
  const customerInteractionState = useSelector(
    (state: RootState) => state.careerRoadmap.customer_interaction,
  );
  const [physicalityScore, setPhysicalityScore] = useState(
    physicalityState?.answer ? physicalityState.answer : '',
  );
  const [facilityScore, setFacilityScore] = useState(
    facilityState?.answer ? facilityState.answer : '',
  );
  const [customerInteractionScore, setCustomerInteractionScore] = useState(
    customerInteractionState?.answer ? customerInteractionState.answer : '',
  );

  const currentUser: CustomUser | null = useAuthContext();
  const [currsentAsaTask, setCurrentAsaTask] = useState<AsaUserTasks | undefined>(undefined);
  const asaUserTaskHistory = useUserAsaTaskHistoryContext();

  useEffect(() => {
    if (
      currentUser?.claims?.isAsaUser &&
      Object.keys(asaUserTaskHistory).length &&
      asaUserTaskHistory?.tasks?.length
    ) {
      const currentTask = asaUserTaskHistory.tasks?.find(
        (x) => !x.isCompleted && x.taskName === AsaTaskName.CAREERROADMAP,
      );

      setCurrentAsaTask(currentTask);

      if (currentTask && !currentTask?.startDate) {
        void dispatch(
          dispatchUpdateAsaTaskHistoryInfo({
            asaUserId: currentUser?.claims?.asaUserId || 0,
            taskId: currentTask?.taskId || '',
            taskStep: currentTask?.taskStep || 1,
            startDate: currentTask?.startDate || new Date(),
            taskName: AsaTaskName.CAREERROADMAP,
          }),
        );
      }

      if (currsentAsaTask && (currsentAsaTask?.taskStep as number) != 11) {
        if (currsentAsaTask?.taskUrl) {
          const url = new URL(currsentAsaTask?.taskUrl || '');
          navigate(`${url.pathname}${url.search}`);
          // setIsDisabled(true);
        }
      }
    }
  }, [asaUserTaskHistory, currentUser, currsentAsaTask]);

  const backButtonClick = () => {
    onBack();
  };

  useEffect(() => {
    if (!physicalityScore && physicalityState?.answer) {
      setPhysicalityScore(physicalityState?.answer);
    }

    if (!facilityScore && facilityState?.answer) {
      setFacilityScore(facilityState?.answer);
    }

    if (!customerInteractionScore && customerInteractionState?.answer) {
      setCustomerInteractionScore(customerInteractionState?.answer);
    }
  }, [physicalityState?.answer, facilityState?.answer, customerInteractionState?.answer]);

  const { handleSubmit } = useForm<ScorePhyEnvDto>();

  const handlePhysicalityScore = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhysicalityScore(event.target.value);
    setError('');
  };
  const handleFacilityScore = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFacilityScore(event.target.value);
    setError('');
  };
  const handleCustomerInteractionScore = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerInteractionScore(event.target.value);
    setError('');
  };

  const submitCareerRoadMapAnswer: SubmitHandler<FormData> = async (data: FormData) => {
    logger.debug('Submitted personal info.', data);
    setIsLoading(true);

    try {
      if (!physicalityScore || !facilityScore || !customerInteractionScore) {
        setError('Please answer all three questions before continuing.');
        return;
      }
      if (data) {
        if (physicalityScore) {
          const question12: CareerRoadMapDto = {
            id: roadMapQuestions[11].questionNumber,
            answer: physicalityScore,
            question: roadMapQuestions[11].question,
          };
          if (!physicalityState?.answer || physicalityState.answer !== physicalityScore) {
            question12 &&
              (await dispatch(dispatchCareerRoadMapAssessment(question12, 'physicality_score')));
          }
        }

        if (facilityScore) {
          const question13: CareerRoadMapDto = {
            id: roadMapQuestions[12].questionNumber,
            answer: facilityScore,
            question: roadMapQuestions[12].question,
          };
          if (!facilityState?.answer || facilityState.answer !== facilityScore) {
            question13 &&
              (await dispatch(dispatchCareerRoadMapAssessment(question13, 'facility_environment')));
          }
        }

        if (customerInteractionScore) {
          const question14: CareerRoadMapDto = {
            id: roadMapQuestions[13].questionNumber,
            answer: customerInteractionScore,
            question: roadMapQuestions[13].question,
          };
          if (
            !customerInteractionState?.answer ||
            customerInteractionState.answer !== customerInteractionScore
          ) {
            question14 &&
              (await dispatch(dispatchCareerRoadMapAssessment(question14, 'customer_interaction')));
          }
        }

        if (currentUser?.claims?.isAsaUser && currsentAsaTask) {
          const endPointUrl = process.env[EnvironmentVariable.REACT_APP_ASA_COMPLETE_TASK_URL];
          const isStaging =
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('staging') ||
            process.env[EnvironmentVariable.REACT_APP_BASE_URL]?.includes('localhost');
          const taskResponse = await completeASATask(
            {
              asaUserId: currentUser?.claims?.asaUserId as number,
              taskId: currsentAsaTask?.taskId,
              taskStep: currsentAsaTask?.taskStep || step,
            },
            endPointUrl || '',
            isStaging || false,
          );

          if (!taskResponse || !taskResponse?.ok) {
            toast.error('Something went wrong!, Please try after sometime.');
            return;
          }

          let url = new URL(currsentAsaTask?.taskUrl || '');
          const nextTaskUrl = buildASANextTaskUrl(url, AsaTaskName.CAREERROADMAP, step + 1);
          url = new URL(nextTaskUrl || '');
          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              startDate: new Date(),
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep:
                (currsentAsaTask && parseInt(currsentAsaTask?.taskStep.toString()) + 1) || 11,
              taskUrl: nextTaskUrl,
              taskName: AsaTaskName.CAREERROADMAP,
              totalSteps: 17,
            }),
          );

          await dispatch(
            dispatchUpdateAsaTaskHistoryInfo({
              asaUserId: currentUser?.claims?.asaUserId || 0,
              taskId: currsentAsaTask?.taskId || '',
              taskStep: currsentAsaTask?.taskStep || step,
              isCompleted: true,
              completedDate: new Date(),
            }),
          );
          toast.success('Task has been successfully submitted');
          navigate(`${url.pathname}${url.search}`);
        } else {
          navigate(`/career-roadmap/step${step + 1}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
      setError('Something went wrong');
    } finally {
      setIsLoading(false);
      setError('');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div>
      {isLoading && <CircularLoader />}

      <div className="character absolute bottom-0 right-[-85px] lg:right-[-138px]">
        <img className="" src={character} alt="" />
      </div>
      <div className=" mt-9 mb-10 md:px-10">
        <form
          className="md:px-10"
          action=""
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(submitCareerRoadMapAnswer)}
        >
          <div>
            <div className="flex mt-9 mb-10 ">
              <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
                {roadMapQuestions[11].questionNumber}
              </div>
              <div>
                <h4 className="text-2xl">
                  <span className="font-semibold">{roadMapQuestions[11].question} </span>
                </h4>
              </div>
            </div>
            <div className="border rounded-lg flex items-center gap-5 p-6 mb-3">
              <div className="lang-txt  flex  font-semibold">Rate</div>
              <div className="rating-btns flex justify-end flex-wrap basis-full gap-4">
                {proficiency_options.map((option) => (
                  <div key={option.value} className="h-[35px] w-[44px]">
                    <label className="radio-container h-full block relative">
                      <input
                        // disabled={isDisabled}
                        checked={physicalityScore === option.value}
                        onChange={handlePhysicalityScore}
                        required
                        value={option.value}
                        className="w-full h-full absolute top-0 left-0 opacity-0"
                        type="radio"
                        name="physicality"
                      />
                      <span className="checkmark">{option.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="flex mt-9 mb-10 ">
              <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
                {roadMapQuestions[12].questionNumber}
              </div>
              <div>
                <h4 className="text-2xl">
                  <span className="font-semibold">{roadMapQuestions[12].question} </span>
                </h4>
              </div>
            </div>
            <div className="border rounded-lg flex items-center gap-5 p-6 mb-3">
              <div className="lang-txt  flex  font-semibold">Rate</div>
              <div className="rating-btns flex justify-end flex-wrap basis-full gap-4">
                {proficiency_options.map((option) => (
                  <div key={option.value} className="h-[35px] w-[44px]">
                    <label className="radio-container h-full block relative">
                      <input
                        // disabled={isDisabled}
                        checked={facilityScore === option.value}
                        onChange={handleFacilityScore}
                        required
                        value={option.value}
                        className="w-full h-full absolute top-0 left-0 opacity-0"
                        type="radio"
                        name="facility"
                      />
                      <span className="checkmark">{option.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div className="flex mt-9 mb-10 ">
              <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">
                {roadMapQuestions[13].questionNumber}
              </div>
              <div>
                <h4 className="text-2xl">
                  <span className="font-semibold">{roadMapQuestions[13].question} </span>
                </h4>
              </div>
            </div>
            <div className="border rounded-lg flex items-center gap-5 p-6 mb-3">
              <div className="lang-txt  flex  font-semibold">Rate</div>
              <div className="rating-btns flex justify-end flex-wrap basis-full gap-4">
                {proficiency_options.map((option) => (
                  <div key={option.value} className="h-[35px] w-[44px]">
                    <label className="radio-container h-full block relative">
                      <input
                        // disabled={isDisabled}
                        checked={customerInteractionScore === option.value}
                        onChange={handleCustomerInteractionScore}
                        required
                        value={option.value}
                        className="w-full h-full absolute top-0 left-0 opacity-0"
                        type="radio"
                        name="customerInteraction"
                      />
                      <span className="checkmark">{option.label}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end lg:justify-between mt-8 ml-5">
            <button
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleSubmit(backButtonClick)}
              className="btn-internal"
            >
              Back
            </button>
            <button className="btn-internal ml-5" disabled={isLoading}>
              Next
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
